import { useEffect, useState } from 'react';

import { IHistoricalOrder, IServerOrder } from '@rbi-ctg/menu';
import { IImageFragment } from 'generated/sanity-graphql';
import { MappedCartEntry, MappedOrder } from 'pages/menu/components/menu-reorder-widget/types';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { useUIContext } from 'state/ui';
import logger from 'utils/logger';
import { parseStringifiedJSON } from 'utils/parse-string';

type ItemHighlighted = {
  name: string;
  image: IImageFragment;
};

interface UseOrderDetailsReturn {
  totalPrice: string;
  itemHighlighted: ItemHighlighted | null;
  totalItems: number;
  itemNames: string;
}

function isMappedOrder(order: IServerOrder | IHistoricalOrder): order is MappedOrder {
  return 'orderEntries' in order;
}

export const useOrderDetailsCard = (
  order: IServerOrder | IHistoricalOrder,
  isReorder: boolean = false
): UseOrderDetailsReturn => {
  const { formatCurrencyForLocale } = useUIContext();
  const mapToFrontendEntry = useMapToFrontendCartEntry();

  const {
    cart: { cartEntries, totalCents },
  } = order;

  const entries = cartEntries ?? [];
  const orderDetailsEntries: MappedCartEntry[] =
    isReorder && isMappedOrder(order) ? order.orderEntries : [];
  const totalItems = entries.reduce((total, cartEntry) => total + (cartEntry.quantity ?? 1), 0);
  const totalPrice = formatCurrencyForLocale(totalCents);
  const itemNames =
    isReorder && isMappedOrder(order)
      ? orderDetailsEntries.map(item => item.name).join(' - ')
      : entries.map(item => item.name).join(' - ');

  const [itemHighlighted, setItemHighlighted] = useState<ItemHighlighted>({
    name: entries[0]?.name || '',
    image: parseStringifiedJSON({
      value: entries[0]?.image || '',
    }),
  });

  useEffect(() => {
    // For One Menu stores, the image is not included in the order cart entries,
    // so we need to retrieve it by mapping from the frontend cart entry.
    if (!itemHighlighted.image && !!cartEntries?.[0]) {
      mapToFrontendEntry(cartEntries?.[0])
        .then(data => {
          if (!data?.cartEntry?.name || !data?.cartEntry?.image) {
            return;
          }

          setItemHighlighted({
            name: data.cartEntry.name,
            image: data.cartEntry.image as IImageFragment,
          });
        })
        .catch(error => {
          logger.warn({
            error,
            message: `Error mapping reorder cart entry: ${cartEntries?.[0].sanityId}`,
          });
        });
    }
  }, [cartEntries, itemHighlighted.image, mapToFrontendEntry]);

  return {
    totalItems,
    totalPrice,
    itemHighlighted,
    itemNames,
  };
};
